import { colorsPalettes, rgba } from "@similarweb/styles";
import { setFont } from "@similarweb/styles";
import { PopupHoverContainer } from "@similarweb/ui-components";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import styled from "styled-components";
import { i18nFilter } from "app/filters/i18nFilter";
import type { ISiteSerpFeatures } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellUtils";
import type { ReactNode } from "react";
import {
    SerpIconComponent,
    SerpIconsComponentWithText,
} from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpStyledComponents";
import { SERP_MAP } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTypes";
import type { ISerpFeaturesMap } from "app/common/serp/SerpFilterMaps";
const Title = styled.div`
    ${setFont({ $size: 16, $color: rgba(colorsPalettes.carbon["500"], 0.8), $weight: 500 })};
    margin-bottom: 4px;
    overflow-wrap: break-word;
`;
const Subtitle = styled.div`
    ${setFont({ $size: 12, $color: rgba(colorsPalettes.carbon["500"], 0.6) })};
`;
const FeaturedTitle = styled.div`
    ${setFont({ $size: 14, $color: rgba(colorsPalettes.carbon["500"], 0.8) })};
    margin-bottom: 12px;
    margin-top: 16px;
    overflow-wrap: break-word;
`;
const FeaturedRowsContainer = styled.div<{ marginTop?: string }>`
    ${({ marginTop = "0px" }) => `margin-top:${marginTop}`}
`;
const Row = styled.div`
    margin-bottom: 8px;
`;

export interface ISerpTooltipContentProps {
    serpFeatures: ISiteSerpFeatures;
    serpFeaturesMap: ISerpFeaturesMap;
    showSerpFeaturesTitle?: boolean;
}

const SerpTooltipContent: React.FC<ISerpTooltipContentProps> = (props) => {
    const { serpFeatures, serpFeaturesMap, showSerpFeaturesTitle = true } = props;
    const hasFeaturedSerp = Object.keys(serpFeatures.featured).length > 0;
    const hasNonFeaturedSerp = serpFeatures.nonFeatured.length > 0;
    return (
        <FlexColumn>
            <Title>
                {i18nFilter()("serp.tooltip.title.forkeyword", {
                    keyword: serpFeatures.searchTerm,
                })}
            </Title>
            <Subtitle>
                {serpFeatures.date &&
                    i18nFilter()("serp.tooltip.date", {
                        date: serpFeatures.date.format("MMM DD, YYYY"),
                    })}
            </Subtitle>
            {hasFeaturedSerp && (
                <>
                    {showSerpFeaturesTitle && (
                        <FeaturedTitle>
                            {i18nFilter()("serp.tooltip.featured.in", {
                                name: serpFeatures.sites[0].name,
                            })}
                        </FeaturedTitle>
                    )}
                    <FeaturedRowsContainer marginTop={showSerpFeaturesTitle ? "0px" : "16px"}>
                        {Object.keys(serpFeatures.featured).map((serp) => {
                            return (
                                <Row key={serpFeaturesMap[serp].name}>
                                    <SerpIconsComponentWithText text={serpFeaturesMap[serp].name}>
                                        <SerpIconComponent
                                            background={colorsPalettes.blue["200"]}
                                            iconName={serpFeaturesMap[serp].icon}
                                        />
                                    </SerpIconsComponentWithText>
                                </Row>
                            );
                        })}
                    </FeaturedRowsContainer>
                </>
            )}
            {hasNonFeaturedSerp && (
                <>
                    <FeaturedTitle>{i18nFilter()(hasFeaturedSerp ? "serp.tooltip.featured.other" : "")}</FeaturedTitle>
                    {serpFeatures.nonFeatured.map((serp) => {
                        return (
                            <Row key={serpFeaturesMap[serp].name}>
                                <SerpIconsComponentWithText text={serpFeaturesMap[serp].name}>
                                    <SerpIconComponent iconName={serpFeaturesMap[serp].icon} />
                                </SerpIconsComponentWithText>
                            </Row>
                        );
                    })}
                </>
            )}
        </FlexColumn>
    );
};

export interface ISerpTableCellTooltipProps {
    serpFeatures: ISiteSerpFeatures;
    serpFeaturesMap?: ISerpFeaturesMap;
    showSerpFeaturesTitle?: boolean;
    children: ReactNode;
}
export const SerpTableCellTooltipSingle: React.FC<ISerpTableCellTooltipProps> = (props) => {
    const { serpFeatures, children, serpFeaturesMap = SERP_MAP, showSerpFeaturesTitle = true } = props;
    return (
        <PopupHoverContainer
            config={{ enabled: true, width: 309, placement: "right" }}
            content={() => (
                <SerpTooltipContent
                    serpFeatures={serpFeatures}
                    serpFeaturesMap={serpFeaturesMap}
                    showSerpFeaturesTitle={showSerpFeaturesTitle}
                />
            )}
        >
            {children}
        </PopupHoverContainer>
    );
};
